import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import { NavItem } from './NavItem';
import { Link } from 'dibs-elements/exports/Link';
import { trackNavInteraction } from '../../helpers/tracking';
import { VatReportModal } from '../VatReportModal/VatReportModal';
import { useVatReportModalOpenState } from '../NavItems/helpers';

import { type NavItemVATReportLink_seller$key } from './__generated__/NavItemVATReportLink_seller.graphql';
import { type NavItemVATReportLink_navItem$key } from './__generated__/NavItemVATReportLink_navItem.graphql';
import { type NavItemVATReportLink_notifications$key } from './__generated__/NavItemVATReportLink_notifications.graphql';

type Props = {
    seller?: NavItemVATReportLink_seller$key | null;
    navItem: NavItemVATReportLink_navItem$key;
    notifications?: NavItemVATReportLink_notifications$key | null;
    isActive?: boolean;
};

const sellerFragment = graphql`
    fragment NavItemVATReportLink_seller on Seller {
        ...NavItem_seller
        ...VatReportModal_seller
    }
`;

const navItemFragment = graphql`
    fragment NavItemVATReportLink_navItem on NavBarElement {
        ...NavItem_navItem
        eventName
        interactionType
        href
        title
    }
`;

const notificationsFragment = graphql`
    fragment NavItemVATReportLink_notifications on DealerNavBarBadges {
        ...NavItem_notifications
    }
`;

export const NavItemVATReportLink: FC<Props> = ({
    seller: sellerRef,
    navItem: navItemRef,
    notifications: notificationsRef,
    isActive,
}) => {
    const seller = useFragment(sellerFragment, sellerRef);
    const navItem = useFragment(navItemFragment, navItemRef);
    const notifications = useFragment(notificationsFragment, notificationsRef);

    const { title, href, eventName, interactionType } = navItem;

    const { isVatReportModalOpen, setIsVatReportModalOpen, vatReportModalHandler } =
        useVatReportModalOpenState(href || '');

    return (
        <Link
            key={title}
            href={href || ''}
            onClick={event => {
                trackNavInteraction({
                    action: eventName,
                    label: eventName,
                    interactionType,
                });
                vatReportModalHandler(event);
            }}
        >
            <NavItem
                seller={seller}
                navItem={navItem}
                notifications={notifications}
                isActive={isActive}
                isExpandingAreaItem
            />
            <VatReportModal
                seller={seller}
                isOpen={isVatReportModalOpen}
                onClose={() => setIsVatReportModalOpen(false)}
            />
        </Link>
    );
};
